import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { getNewsById, getLocalizedNews, newsData } from '../../data/newsData';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);
  const [fontSize, setFontSize] = React.useState(18);
  const [isSpeaking, setIsSpeaking] = React.useState(false);

  const news = getNewsById(id);
  const localizedNews = getLocalizedNews(news, language);

  // İlginizi Çekebilir (Aynı kategoriden rastgele 3 haber)
  const relatedNews = React.useMemo(() => {
    if (!news) return [];
    const sameCategory = newsData.filter(item => item.category === news.category && item.id !== news.id);
    return sameCategory.sort(() => 0.5 - Math.random()).slice(0, 3).map(item => getLocalizedNews(item, language));
  }, [news, language]);

  // Okuma süresi hesaplama
  const readingTime = React.useMemo(() => {
    if (!localizedNews?.content) return 0;
    const text = localizedNews.content.trim();
    const words = text ? text.split(/\s+/).length : 0;
    return Math.max(1, Math.ceil(words / 200)); // En az 1 dakika, ortalama 200 kelime/dk
  }, [localizedNews]);

  // Sesli okuma (TTS) fonksiyonu
  const handleSpeak = () => {
    if ('speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      } else {
        const cleanContent = localizedNews.content.replace(/\*\*/g, '');
        const text = `${localizedNews.title}. ${cleanContent}`;
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = language === 'tr' ? 'tr-TR' : 'en-US';
        utterance.rate = 0.9; // Biraz daha anlaşılır olması için hız
        
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
        setIsSpeaking(true);
      }
    }
  };

  // Sayfadan ayrılınca okumayı durdur
  React.useEffect(() => {
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderContent = (content) => {
    return content.split('\n\n').map((paragraph, index) => {
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return <h3 key={index} style={{...styles.subheading, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>{paragraph.replace(/\*\*/g, '')}</h3>;
      }
      if (paragraph.includes('**')) {
        const parts = paragraph.split('**');
        return <p key={index} style={{...styles.paragraph, fontSize: `${fontSize}px`, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700}}>{parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}</p>;
      }
      return <p key={index} style={{...styles.paragraph, fontSize: `${fontSize}px`, color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray700}}>{paragraph}</p>;
    });
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  if (!localizedNews) {
    return (
      <div style={{...styles.page, backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white}}>
        <div style={styles.notFound}>
          <h1 style={{ color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark }}>{language === 'tr' ? 'Haber bulunamadı' : 'News not found'}</h1>
          <button onClick={() => navigate('/news')} style={{...styles.backButton, backgroundColor: isDarkMode ? DARK_THEME.primary : COLORS.primary}}>{language === 'tr' ? 'Haberlere Dön' : 'Back to News'}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Hero with Image */}
      <section style={{...styles.hero, backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${localizedNews.image})`}}>
        <div style={styles.heroOverlay}></div>
        <button 
          onClick={() => setIsLightboxOpen(true)}
          style={styles.expandButton}
          title={language === 'tr' ? 'Tam Ekran' : 'Full Screen'}
        >
          ⤢
        </button>
        <div style={styles.heroContent}>
          {/* Breadcrumb */}
          <Link to="/news" style={styles.breadcrumbLink}>{language === 'tr' ? '← Haberlere Dön' : '← Back to News'}</Link>
          <div style={styles.meta}>
            <span style={styles.categoryTag}>{localizedNews.category}</span>
            <span style={styles.date}>{formatDate(localizedNews.date)}</span>
            <span style={styles.readingTime}>• {readingTime} {language === 'tr' ? 'dk okuma' : 'min read'}</span>
          </div>
          <h1 style={styles.heroTitle}>{localizedNews.title}</h1>
        </div>
      </section>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div style={styles.lightboxOverlay} onClick={() => setIsLightboxOpen(false)}>
          <button style={styles.lightboxCloseBtn} onClick={() => setIsLightboxOpen(false)}>✕</button>
          <img 
            src={localizedNews.image} 
            alt={localizedNews.title} 
            style={styles.lightboxImage} 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}

      {/* Content */}
      <section style={{...styles.section, backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white}}>
        <div style={styles.container}>
          <article style={{...styles.article, backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white}}>
            {/* Lead paragraph */}
            <p style={{...styles.lead, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>{localizedNews.excerpt}</p>

            {/* Font Size Controls */}
            <div style={styles.fontControls}>
              <button 
                onClick={handleSpeak} 
                style={{
                  ...styles.fontBtn, 
                  marginRight: 'auto', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  borderColor: isSpeaking ? COLORS.primary : (isDarkMode ? DARK_THEME.border : COLORS.gray300),
                  color: isSpeaking ? COLORS.primary : (isDarkMode ? DARK_THEME.textPrimary : COLORS.dark)
                }}
              >
                {isSpeaking ? (
                  <><span>🔇</span> {language === 'tr' ? 'Durdur' : 'Stop'}</>
                ) : (
                  <><span>🔊</span> {language === 'tr' ? 'Sesli Oku' : 'Listen'}</>
                )}
              </button>
              <button onClick={() => setFontSize(Math.max(14, fontSize - 2))} style={{...styles.fontBtn, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark, borderColor: isDarkMode ? DARK_THEME.border : COLORS.gray300}}>A-</button>
              <button onClick={() => setFontSize(Math.min(24, fontSize + 2))} style={{...styles.fontBtn, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark, borderColor: isDarkMode ? DARK_THEME.border : COLORS.gray300}}>A+</button>
            </div>

            <div style={{...styles.divider, backgroundColor: isDarkMode ? DARK_THEME.border : COLORS.gray300}}></div>

            {/* Main content */}
            <div style={styles.content}>{renderContent(localizedNews.content)}</div>

            {/* Share section */}
            <div style={{...styles.shareSection, borderTop: `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}`}}>
              <span style={{color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>{language === 'tr' ? 'Paylaş:' : 'Share:'}</span>
              <div style={styles.shareButtons}>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" style={{...styles.shareButton, backgroundColor: '#1DA1F2'}}>𝕏</a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" style={{...styles.shareButton, backgroundColor: '#4267B2'}}>f</a>
              </div>
            </div>
          </article>

          {/* Related News Section */}
          {relatedNews.length > 0 && (
            <div style={{...styles.relatedSection, borderTop: `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}`}}>
              <h3 style={{...styles.relatedTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                {language === 'tr' ? 'İlginizi Çekebilir' : 'You Might Also Like'}
              </h3>
              <div style={styles.relatedGrid}>
                {relatedNews.map(item => (
                  <Link to={`/news/${item.id}`} key={item.id} style={styles.relatedCard} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    <div style={{...styles.relatedImage, backgroundImage: `url(${item.image})`}}></div>
                    <div style={styles.relatedContent}>
                      <span style={{...styles.relatedDate, color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>{formatDate(item.date)}</span>
                      <h4 style={{...styles.relatedCardTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>{item.title}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back button */}
          <div style={styles.backSection}>
            <button onClick={() => navigate('/news')} style={{...styles.backButtonLarge, backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.light, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark, border: `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}`}}>{language === 'tr' ? '← Tüm Haberlere Dön' : '← Back to All News'}</button>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  page: { paddingTop: '0px' },
  hero: { height: '50vh', minHeight: '400px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', position: 'relative', backgroundSize: 'cover', backgroundPosition: 'center' },
  heroOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0L60 30L30 60L0 30z\' fill=\'rgba(255,255,255,0.03)\'/%3E%3C/svg%3E")' },
  heroContent: { position: 'relative', zIndex: 2, width: '100%', maxWidth: '900px', padding: '40px 20px', margin: '0 auto' },
  breadcrumbLink: { color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '20px' },
  meta: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' },
  categoryTag: { backgroundColor: COLORS.primary, color: COLORS.white, padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  date: { color: 'rgba(255,255,255,0.8)', fontSize: '14px' },
  readingTime: { color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: '500' },
  heroTitle: { fontSize: 'clamp(28px, 5vw, 42px)', color: COLORS.white, fontWeight: '700', lineHeight: '1.3' },
  section: { padding: '60px 20px 80px' },
  container: { maxWidth: '900px', margin: '0 auto' },
  article: { borderRadius: '20px', padding: '40px', marginTop: '-60px', position: 'relative', zIndex: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' },
  lead: { fontSize: '20px', lineHeight: '1.7', fontWeight: '500', marginBottom: '30px' },
  divider: { height: '1px', marginBottom: '30px', marginTop: '20px' },
  content: {},
  subheading: { fontSize: '22px', fontWeight: '700', marginTop: '35px', marginBottom: '15px' },
  paragraph: { fontSize: '17px', lineHeight: '1.8', marginBottom: '20px' },
  shareSection: { marginTop: '40px', paddingTop: '30px', display: 'flex', alignItems: 'center', gap: '15px' },
  shareButtons: { display: 'flex', gap: '10px' },
  shareButton: { width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.white, textDecoration: 'none', fontWeight: '700', fontSize: '16px' },
  backSection: { marginTop: '40px', textAlign: 'center' },
  backButtonLarge: { padding: '16px 32px', borderRadius: '50px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  notFound: { minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' },
  backButton: { color: COLORS.white, padding: '14px 28px', borderRadius: '50px', border: 'none', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  expandButton: {
    position: 'absolute',
    top: '90px',
    right: '30px',
    zIndex: 10,
    background: 'rgba(255,255,255,0.2)',
    backdropFilter: 'blur(5px)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '50%',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '20px',
    transition: 'all 0.3s ease',
  },
  lightboxOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.95)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    cursor: 'zoom-out',
  },
  lightboxImage: {
    maxWidth: '100%',
    maxHeight: '90vh',
    objectFit: 'contain',
    borderRadius: '4px',
    cursor: 'default',
    boxShadow: '0 0 40px rgba(0,0,0,0.5)',
  },
  lightboxCloseBtn: {
    position: 'absolute',
    top: '30px',
    right: '30px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '24px',
    cursor: 'pointer',
    zIndex: 10000,
    transition: 'background 0.3s ease',
  },
  fontControls: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginBottom: '10px' },
  fontBtn: { padding: '5px 12px', borderRadius: '6px', border: '1px solid', background: 'transparent', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: 'all 0.2s' },
  relatedSection: { marginTop: '60px', paddingTop: '40px' },
  relatedTitle: { fontSize: '24px', fontWeight: '700', marginBottom: '30px' },
  relatedGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px' },
  relatedCard: { textDecoration: 'none', display: 'block', transition: 'transform 0.2s' },
  relatedImage: { height: '160px', borderRadius: '12px', backgroundSize: 'cover', backgroundPosition: 'center', marginBottom: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' },
  relatedDate: { fontSize: '12px', marginBottom: '5px', display: 'block' },
  relatedCardTitle: { fontSize: '16px', fontWeight: '600', lineHeight: '1.4' },
};

export default NewsDetail;
